import React, { useState, useCallback, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { QUIZ, QUIZ_LENGTH } from '../data/quizData';
import { LandingScreen } from './LandingScreen';
import { QuestionScreen } from './QuestionScreen';
import { MicroInsightScreen } from './MicroInsightScreen';
import { EmailCaptureScreen } from './EmailCaptureScreen';
import { ResultsScreen } from './ResultsScreen';
import { AnnouncementBar } from './AnnouncementBar';
import { fbqTrack } from '../lib/fbq';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Steps: 'landing' | 'question' | 'insight' | 'email' | 'results'
export const QuizFunnel = () => {
  const [step, setStep] = useState('landing');
  const [qIndex, setQIndex] = useState(0);
  const [answers, setAnswers] = useState([]); // [{ question_id, question_text, answer, answer_index }]
  const [sessionId, setSessionId] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [step, qIndex]);

  const startQuiz = useCallback(async () => {
    setStep('question');
    setQIndex(0);
    fbqTrack('ViewContent', { content_name: 'CoQ10 Quiz Started', content_category: 'Quiz' });
    try {
      const { data } = await axios.post(`${API}/quiz/start`);
      setSessionId(data.session_id);
    } catch (e) {
      // Non-blocking — continue locally
      console.warn('quiz/start failed', e);
    }
  }, []);

  const handleAnswer = useCallback(
    async (answer, answerIndex) => {
      const q = QUIZ[qIndex];
      const record = {
        question_id: q.id,
        question_text: q.question,
        answer,
        answer_index: answerIndex,
      };
      const next = [...answers.filter((a) => a.question_id !== q.id), record];
      setAnswers(next);

      // Fire-and-forget partial save
      if (sessionId) {
        axios
          .post(`${API}/quiz/answer`, { session_id: sessionId, ...record })
          .catch((e) => console.warn('quiz/answer failed', e));
      }

      setStep('insight');
    },
    [qIndex, answers, sessionId]
  );

  const handleInsightContinue = useCallback(() => {
    if (qIndex + 1 < QUIZ_LENGTH) {
      setQIndex(qIndex + 1);
      setStep('question');
    } else {
      setStep('email');
    }
  }, [qIndex]);

  const handleBack = useCallback(() => {
    if (qIndex > 0) {
      setQIndex(qIndex - 1);
      setStep('question');
    }
  }, [qIndex]);

  const handleEmailSubmit = useCallback(
    async ({ firstName: fn, email }) => {
      setSubmitting(true);
      setError(null);
      setFirstName(fn);
      try {
        await axios.post(`${API}/leads`, {
          session_id: sessionId,
          first_name: fn,
          email,
          answers,
        });
        fbqTrack('Lead', { content_name: 'CoQ10 Quiz Completed', content_category: 'Quiz' });
        setStep('results');
      } catch (e) {
        console.error('lead submit failed', e);
        setError('Something went wrong saving your results. Please try again.');
      } finally {
        setSubmitting(false);
      }
    },
    [sessionId, answers]
  );

  return (
    <>
      {step !== 'results' && <AnnouncementBar />}
      <AnimatePresence mode="wait">
        {step === 'landing' && <LandingScreen key="landing" onStart={startQuiz} />}

        {step === 'question' && (
          <QuestionScreen
            key={`q-${QUIZ[qIndex].id}`}
            question={QUIZ[qIndex]}
            total={QUIZ_LENGTH}
            onAnswer={handleAnswer}
            onBack={handleBack}
            canBack={qIndex > 0}
          />
        )}

        {step === 'insight' && (
          <MicroInsightScreen
            key={`insight-${QUIZ[qIndex].id}`}
            insight={QUIZ[qIndex].insight}
            overline={QUIZ[qIndex].overline}
            onContinue={handleInsightContinue}
          />
        )}

        {step === 'email' && (
          <EmailCaptureScreen
            key="email"
            onSubmit={handleEmailSubmit}
            submitting={submitting}
            error={error}
          />
        )}

        {step === 'results' && (
          <ResultsScreen key="results" firstName={firstName} />
        )}
      </AnimatePresence>
    </>
  );
};
